import { trackKlaviyo, trackKlaviyoViewedItem } from './klaviyoOnsite'

// Metric names are used by Klaviyo flows and segments, keep them exact
export const KLAVIYO_METRICS = {
  VIEWED_PRODUCT: 'Viewed Product',
  ADDED_TO_CART: 'Added to Cart',
  CONTACTED_SALES: 'Contacted Sales',
  DOWNLOADED_RESOURCE: 'Downloaded Resource',
  VIEWED_INDUSTRY_PAGE: 'Viewed Industry Page',
  VIEWED_SERVICE_PAGE: 'Viewed Service Page',
  SEARCHED_SITE: 'Searched Site',
}

const SERVICE_PAGES = [
  { path: '/drone-training', name: 'Training' },
  { path: '/dji-drone-repair-centre', name: 'Repairs' },
  { path: '/drone-hire', name: 'Rental' },
  { path: '/drone-as-first-responder-for-public-safety', name: 'DFR' },
  { path: '/drones-for-utilities', name: 'Utilities' },
  { path: '/drones-security-and-surveillance', name: 'Security' },
  { path: '/reality-capture-3dgs', name: 'Reality Capture' },
]

const INDUSTRY_PATH_PREFIX = '/industry/'

const toNumber = (value) => {
  const number = parseFloat(value)
  return Number.isFinite(number) ? number : undefined
}

const stripGid = (gid) => (typeof gid === 'string' ? gid.split('/').pop() : gid)

const isPathOrChild = (pathname, path) => pathname === path || pathname.startsWith(`${path}/`)

// "/industry/drones-for-construction/" -> "construction", "/industry/police-drones/" -> "police"
const getIndustrySegment = (pathname) => {
  const slug = pathname.slice(INDUSTRY_PATH_PREFIX.length).split('/')[0]
  if (!slug) return null

  return (
    slug
      .replace(/^drones-for-/, '')
      .replace(/-drones(-uavs)?$/, '')
      .replace(/^drone-/, '') || slug
  )
}

export function trackViewedProduct(shopifyProduct, url) {
  if (!shopifyProduct) return

  const variant = shopifyProduct.variants?.[0]
  const productId = stripGid(shopifyProduct.shopifyId)
  const imageUrl =
    shopifyProduct.featuredImage?.originalSrc ||
    shopifyProduct.media?.find((media) => media?.preview?.image?.originalSrc)?.preview?.image
      ?.originalSrc
  const categories = (shopifyProduct.collections || []).map(({ title }) => title).filter(Boolean)
  const price = toNumber(variant?.price)
  const compareAtPrice = toNumber(variant?.compareAtPrice)

  trackKlaviyo(KLAVIYO_METRICS.VIEWED_PRODUCT, {
    ProductName: shopifyProduct.title,
    ProductID: productId,
    SKU: variant?.sku || undefined,
    Categories: categories,
    ProductType: shopifyProduct.productType || undefined,
    ImageURL: imageUrl,
    URL: url,
    Brand: shopifyProduct.vendor,
    Price: price,
    CompareAtPrice: compareAtPrice,
    $value: price,
  })

  trackKlaviyoViewedItem({
    Title: shopifyProduct.title,
    ItemId: productId,
    Categories: categories,
    ImageUrl: imageUrl,
    Url: url,
    Metadata: {
      Brand: shopifyProduct.vendor,
      Price: price,
      CompareAtPrice: compareAtPrice,
    },
  })
}

/**
 * @param cart Storefront API cart returned after the add
 * @param addedLines [{ merchandiseId, quantity }] that were just added
 */
export function trackAddedToCart(cart, addedLines = []) {
  const cartLines = (cart?.lines?.edges || []).map(({ node }) => node)
  if (!cartLines.length || !addedLines.length) return

  const toItem = (line, quantity) => {
    const merchandise = line?.merchandise
    const product = merchandise?.product

    return {
      VariantID: stripGid(merchandise?.id),
      ProductName: product?.title,
      VariantName: merchandise?.title,
      Quantity: quantity ?? line?.quantity,
      ItemPrice: toNumber(merchandise?.price?.amount),
      ImageURL: product?.featuredImage?.url,
      ProductURL: product?.handle
        ? `${window.location.origin}/products/${product.handle}/`
        : undefined,
    }
  }

  const added = addedLines
    .map(({ merchandiseId, quantity }) => {
      const line = cartLines.find((cartLine) => cartLine?.merchandise?.id === merchandiseId)
      return line ? toItem(line, quantity) : null
    })
    .filter(Boolean)

  if (!added.length) return

  const [mainItem] = added
  const addedValue = added.reduce(
    (sum, item) => sum + (item.ItemPrice || 0) * (item.Quantity || 1),
    0
  )

  trackKlaviyo(KLAVIYO_METRICS.ADDED_TO_CART, {
    $value: toNumber(cart?.cost?.totalAmount?.amount),
    AddedItemProductName: mainItem.ProductName,
    AddedItemVariantID: mainItem.VariantID,
    AddedItemVariantName: mainItem.VariantName,
    AddedItemImageURL: mainItem.ImageURL,
    AddedItemURL: mainItem.ProductURL,
    AddedItemPrice: mainItem.ItemPrice,
    AddedItemQuantity: mainItem.Quantity,
    AddedItemsValue: addedValue,
    ItemNames: cartLines.map((line) => line?.merchandise?.product?.title).filter(Boolean),
    CheckoutURL: cart?.checkoutUrl,
    Items: cartLines.map((line) => toItem(line)),
  })
}

export function trackContactedSales() {
  trackKlaviyo(KLAVIYO_METRICS.CONTACTED_SALES, {
    page_path: window.location.pathname,
  })
}

export function trackSearchedSite(searchTerm, resultsCount) {
  const term = typeof searchTerm === 'string' ? searchTerm.trim() : ''
  if (!term) return

  trackKlaviyo(KLAVIYO_METRICS.SEARCHED_SITE, {
    search_term: term,
    results_count: resultsCount,
  })
}

// Service and industry pages are recognised by URL, so new pages under these paths are covered
export function trackPageCategory(pathname = '') {
  const service = SERVICE_PAGES.find(({ path }) => isPathOrChild(pathname, path))
  if (service) {
    trackKlaviyo(KLAVIYO_METRICS.VIEWED_SERVICE_PAGE, {
      service_name: service.name,
      page_path: pathname,
    })
    return
  }

  if (pathname.startsWith(INDUSTRY_PATH_PREFIX)) {
    const industrySegment = getIndustrySegment(pathname)
    if (industrySegment) {
      trackKlaviyo(KLAVIYO_METRICS.VIEWED_INDUSTRY_PAGE, {
        industry_segment: industrySegment,
        page_path: pathname,
      })
    }
  }
}

const RESOURCE_TYPES = [
  { pattern: /spec(ification)?s?(\s|-|_)?sheet|datasheet|\bspecs?\b/i, type: 'Spec Sheet' },
  { pattern: /case(\s|-|_)?stud/i, type: 'Case Study' },
  { pattern: /guide|manual|handbook|instructions/i, type: 'Guide' },
  { pattern: /brochure|catalogue|catalog/i, type: 'Brochure' },
]

const DOWNLOADABLE_FILE = /\.(pdf|docx?|xlsx?|pptx?|zip)(\?|#|$)/i
const CONTENTFUL_ASSET_HOST = /(^|\.)ctfassets\.net$/i

const getResourceType = (name) =>
  RESOURCE_TYPES.find(({ pattern }) => pattern.test(name))?.type || 'Document'

const getFileName = (url) => {
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').pop() || '')
  } catch {
    return ''
  }
}

const isResourceLink = (link) => {
  try {
    const url = new URL(link.href)
    return (
      DOWNLOADABLE_FILE.test(url.pathname) ||
      (CONTENTFUL_ASSET_HOST.test(url.hostname) &&
        !/\.(png|jpe?g|webp|gif|svg|mp4)$/i.test(url.pathname)) ||
      link.hasAttribute('download')
    )
  } catch {
    return false
  }
}

// One delegated listener covers product documents, the files page and links inside rich text
export function initResourceDownloadTracking() {
  if (typeof document === 'undefined') return

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target?.closest?.('a[href]')
      if (!link || !isResourceLink(link)) return

      const linkText = link.textContent?.replace(/\s+/g, ' ').trim()
      const downloadName = link.getAttribute('download')
      const resourceName = linkText || downloadName || getFileName(link.href)

      trackKlaviyo(KLAVIYO_METRICS.DOWNLOADED_RESOURCE, {
        resource_name: resourceName,
        resource_type: getResourceType(`${resourceName} ${downloadName || ''} ${link.href}`),
        resource_url: link.href,
        page_path: window.location.pathname,
      })
    },
    true
  )
}
